import { useCallback, useEffect, useMemo, useState } from "react";
import * as dfd from "danfojs";
import { Button } from "@/client/components/base/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/client/components/base/card";
import { Input } from "@/client/components/base/input";
import { Label } from "@/client/components/base/label";
import { Spinner } from "@/client/components/base/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/client/components/base/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/client/components/base/tabs";
import { api } from "@/client/lib";

const TRADING_DAYS_PER_YEAR = 252;

type RawRow = {
    timestamp: Date;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    oi: number | null;
};

type DayBar = {
    date: Date;
    open: number;
    close: number;
};

type PeriodKey = "1M" | "3M" | "6M" | "1Y" | "ALL";

const PERIODS: { key: PeriodKey; label: string; months: number | null }[] = [
    { key: "1M", label: "1 Month", months: 1 },
    { key: "3M", label: "3 Months", months: 3 },
    { key: "6M", label: "6 Months", months: 6 },
    { key: "1Y", label: "1 Year", months: 12 },
    { key: "ALL", label: "All History", months: null },
];

/** Summary of one return series: level, direction counts and magnitudes of each side. */
type ReturnStats = {
    count: number;
    avg: number;
    max: number;
    min: number;
    positiveCount: number;
    negativeCount: number;
    /** Avg/min/max of the positive returns themselves. `null` when there were none. */
    positiveAvg: number | null;
    positiveMax: number | null;
    positiveMin: number | null;
    /** Avg/min/max of |negative returns|. `null` when there were none. */
    negativeAvg: number | null;
    negativeMax: number | null;
    negativeMin: number | null;
};

const EMPTY_STATS: ReturnStats = {
    count: 0,
    avg: 0,
    max: 0,
    min: 0,
    positiveCount: 0,
    negativeCount: 0,
    positiveAvg: null,
    positiveMax: null,
    positiveMin: null,
    negativeAvg: null,
    negativeMax: null,
    negativeMin: null,
};

type PeriodAnalysis = {
    period: PeriodKey;
    label: string;
    from: Date;
    to: Date;
    bars: number;
    firstClose: number;
    lastClose: number;
    totalReturnPct: number;
    cagrPct: number;
    volatilityPct: number;
    closeToClose: ReturnStats;
    closeToNextOpen: ReturnStats;
    intraday: ReturnStats;
};

const fmtPct = (value: number | null, digits = 2) =>
    value === null
        ? "—"
        : `${value >= 0 ? "+" : ""}${value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

const fmtPlainPct = (value: number | null, digits = 2) =>
    value === null
        ? "—"
        : `${value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

const fmtPrice = (value: number) =>
    value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const fmtDate = (date: Date) =>
    date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

/** Eden surfaces either a top-level `message` or a validation error under `value.message`. */
function extractErrorMessage(error: unknown): string | undefined {
    if (!error || typeof error !== "object") return undefined;
    const direct = (error as { message?: unknown }).message;
    if (typeof direct === "string") return direct;
    const value = (error as { value?: { message?: unknown } }).value;
    return value && typeof value.message === "string" ? value.message : undefined;
}

function seriesStats(values: number[]): ReturnStats {
    if (values.length === 0) return EMPTY_STATS;
    const series = new dfd.Series(values);
    const positives = values.filter((value) => value > 0);
    const negatives = values.filter((value) => value < 0).map((value) => Math.abs(value));
    const magnitudeStats = (magnitudes: number[]) => {
        if (magnitudes.length === 0) return { avg: null, max: null, min: null };
        const magnitudeSeries = new dfd.Series(magnitudes);
        return {
            avg: Number(magnitudeSeries.mean()) || 0,
            max: Number(magnitudeSeries.max()) || 0,
            min: Number(magnitudeSeries.min()) || 0,
        };
    };
    const positive = magnitudeStats(positives);
    const negative = magnitudeStats(negatives);
    return {
        count: values.length,
        avg: Number(series.mean()) || 0,
        max: Number(series.max()) || 0,
        min: Number(series.min()) || 0,
        positiveCount: positives.length,
        negativeCount: negatives.length,
        positiveAvg: positive.avg,
        positiveMax: positive.max,
        positiveMin: positive.min,
        negativeAvg: negative.avg,
        negativeMax: negative.max,
        negativeMin: negative.min,
    };
}

/** Daily bars within `months` of the last bar, or every bar for the all-history period. */
function slicePeriod(bars: DayBar[], months: number | null): DayBar[] {
    const last = bars[bars.length - 1];
    if (months === null || !last) return bars;
    const cutoff = last.date;
    const from = new Date(cutoff.getFullYear(), cutoff.getMonth() - months, cutoff.getDate());
    return bars.filter((bar) => bar.date.getTime() >= from.getTime());
}

function analysePeriod(bars: DayBar[], period: { key: PeriodKey; label: string; months: number | null }): PeriodAnalysis | null {
    const window = slicePeriod(bars, period.months);
    const first = window[0];
    const last = window[window.length - 1];
    if (!first || !last || window.length < 2) return null;

    const closeToClose: number[] = [];
    const closeToNextOpen: number[] = [];
    const intraday: number[] = [];
    for (let i = 1; i < window.length; i++) {
        const previous = window[i - 1];
        const current = window[i];
        if (!previous || !current) continue;
        closeToClose.push(((current.close - previous.close) / previous.close) * 100);
        closeToNextOpen.push(((current.open - previous.close) / previous.close) * 100);
    }
    for (const bar of window) {
        intraday.push(((bar.close - bar.open) / bar.open) * 100);
    }

    const dailyReturns = closeToClose.map((value) => value / 100);
    const volatilityPct = (Number(new dfd.Series(dailyReturns).std()) || 0) * Math.sqrt(TRADING_DAYS_PER_YEAR) * 100;

    const firstClose = first.close;
    const lastClose = last.close;
    const totalReturnPct = ((lastClose - firstClose) / firstClose) * 100;
    const spanMs = last.date.getTime() - first.date.getTime();
    const years = spanMs / (365.25 * 24 * 60 * 60 * 1000);
    const cagrPct =
        years > 0 && firstClose > 0 && lastClose > 0 ? (Math.pow(lastClose / firstClose, 1 / years) - 1) * 100 : 0;

    return {
        period: period.key,
        label: period.label,
        from: first.date,
        to: last.date,
        bars: window.length,
        firstClose,
        lastClose,
        totalReturnPct,
        cagrPct,
        volatilityPct,
        closeToClose: seriesStats(closeToClose),
        closeToNextOpen: seriesStats(closeToNextOpen),
        intraday: seriesStats(intraday),
    };
}

export default function ReturnsAnalysisPage() {
    const [symbol, setSymbol] = useState("NSE:NIFTY");
    const [input, setInput] = useState("NSE:NIFTY");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [bars, setBars] = useState<DayBar[]>([]);

    const fetchData = useCallback((target: string) => {
        const trimmed = target.trim();
        if (!trimmed) {
            setError("Enter a symbol, e.g. NSE:NIFTY");
            return;
        }
        setLoading(true);
        setError(null);
        api.raw
            .data({ symbol: trimmed })({ interval: "1d" })
            .get()
            .then((res) => {
                if (res.error) {
                    setError(extractErrorMessage(res.error) ?? "Failed to load data");
                    setBars([]);
                    return;
                }
                const rows = (res.data ?? []) as RawRow[];
                const daily: DayBar[] = rows
                    .map((row) => ({
                        date: new Date(row.timestamp),
                        open: row.open,
                        close: row.close,
                    }))
                    .sort((a, b) => a.date.getTime() - b.date.getTime());
                setBars(daily);
                setSymbol(trimmed);
                if (daily.length === 0) {
                    setError(`No daily data found for ${trimmed}`);
                }
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : "Failed to load data");
                setBars([]);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchData(symbol);
        // Only on mount: refreshes re-run via the button.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const analyses = useMemo(
        () => PERIODS.map((period) => analysePeriod(bars, period)).filter((a): a is PeriodAnalysis => a !== null),
        [bars],
    );
    const defaultPeriod = analyses[analyses.length - 1]?.period ?? "ALL";

    return (
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-8 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Returns Analysis</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Daily close-to-close, close-to-next-open, intraday and gap returns with counts, magnitudes,
                        total return and CAGR across rolling periods.
                    </p>
                </div>
                <form
                    className="flex items-end gap-2"
                    onSubmit={(event) => {
                        event.preventDefault();
                        fetchData(input);
                    }}
                >
                    <div className="flex flex-col gap-1">
                        <Label htmlFor="returns-symbol">Symbol</Label>
                        <Input
                            id="returns-symbol"
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            placeholder="NSE:NIFTY"
                            className="w-56"
                        />
                    </div>
                    <Button type="submit" size="sm" disabled={loading}>
                        Analyze
                    </Button>
                </form>
            </div>

            {loading && (
                <div className="flex h-48 items-center justify-center gap-2 text-muted-foreground">
                    <Spinner />
                    Loading daily prices for {symbol}…
                </div>
            )}

            {!loading && error && (
                <div className="flex h-48 flex-col items-center justify-center gap-3 text-center">
                    <p className="text-sm text-destructive">{error}</p>
                    <Button variant="outline" size="sm" onClick={() => fetchData(symbol)}>
                        Retry
                    </Button>
                </div>
            )}

            {!loading && !error && analyses.length === 0 && (
                <div className="flex h-48 items-center justify-center text-muted-foreground">
                    Not enough daily data to compute returns
                </div>
            )}

            {!loading && !error && analyses.length > 0 && (
                <Tabs defaultValue={defaultPeriod} className="w-full">
                    <TabsList>
                        {analyses.map((analysis) => (
                            <TabsTrigger key={analysis.period} value={analysis.period}>
                                {analysis.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {analyses.map((analysis) => (
                        <TabsContent key={analysis.period} value={analysis.period} className="pt-4">
                            <PeriodView analysis={analysis} />
                        </TabsContent>
                    ))}
                </Tabs>
            )}
        </div>
    );
}

function PeriodView({ analysis }: { analysis: PeriodAnalysis }) {
    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <MetricCard
                    label="Total Return"
                    value={fmtPct(analysis.totalReturnPct)}
                    hint={`${fmtPrice(analysis.firstClose)} → ${fmtPrice(analysis.lastClose)}`}
                    tone={analysis.totalReturnPct >= 0 ? "positive" : "negative"}
                />
                <MetricCard
                    label="CAGR"
                    value={fmtPct(analysis.cagrPct)}
                    hint="Annualised, close-to-close"
                    tone={analysis.cagrPct >= 0 ? "positive" : "negative"}
                />
                <MetricCard
                    label="Volatility"
                    value={fmtPlainPct(analysis.volatilityPct)}
                    hint="Annualised, daily stdev"
                />
                <MetricCard label="Trading Days" value={String(analysis.bars)} />
                <MetricCard label="From" value={fmtDate(analysis.from)} />
                <MetricCard label="To" value={fmtDate(analysis.to)} />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Return Series</CardTitle>
                    <CardDescription>
                        Close-to-close spans consecutive days; close-to-next-open is yesterday's close to today's
                        open (the overnight gap); intraday is the same day's open to close.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Series</TableHead>
                                <TableHead className="text-right">Days</TableHead>
                                <TableHead className="text-right">Avg</TableHead>
                                <TableHead className="text-right">Max</TableHead>
                                <TableHead className="text-right">Min</TableHead>
                                <TableHead className="text-right">Positive</TableHead>
                                <TableHead className="text-right">Negative</TableHead>
                                <TableHead className="text-right">Pos Avg</TableHead>
                                <TableHead className="text-right">Pos Max</TableHead>
                                <TableHead className="text-right">Pos Min</TableHead>
                                <TableHead className="text-right">Neg Avg</TableHead>
                                <TableHead className="text-right">Neg Max</TableHead>
                                <TableHead className="text-right">Neg Min</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <StatsRow name="Close → Close" stats={analysis.closeToClose} />
                            <StatsRow name="Close → Next Open (Gap)" stats={analysis.closeToNextOpen} />
                            <StatsRow name="Intraday (Open → Close)" stats={analysis.intraday} />
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Gaps</CardTitle>
                    <CardDescription>Sessions opening above or below the previous close</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                    <MetricCard label="Gap Ups" value={String(analysis.closeToNextOpen.positiveCount)} />
                    <MetricCard label="Gap Downs" value={String(analysis.closeToNextOpen.negativeCount)} />
                    <MetricCard
                        label="Avg Gap Up"
                        value={fmtPlainPct(analysis.closeToNextOpen.positiveAvg)}
                        hint="Magnitude"
                        tone="positive"
                    />
                    <MetricCard
                        label="Avg Gap Down"
                        value={fmtPlainPct(analysis.closeToNextOpen.negativeAvg)}
                        hint="Magnitude"
                        tone="negative"
                    />
                    <MetricCard
                        label="Largest Gap Up"
                        value={fmtPlainPct(analysis.closeToNextOpen.positiveMax)}
                        tone="positive"
                    />
                    <MetricCard
                        label="Largest Gap Down"
                        value={fmtPlainPct(analysis.closeToNextOpen.negativeMax)}
                        tone="negative"
                    />
                </CardContent>
            </Card>
        </div>
    );
}

function StatsRow({ name, stats }: { name: string; stats: ReturnStats }) {
    return (
        <TableRow>
            <TableCell className="font-medium">{name}</TableCell>
            <TableCell className="text-right tabular-nums">{stats.count}</TableCell>
            <TableCell className={`text-right tabular-nums ${pctTone(stats.avg)}`}>{fmtPct(stats.avg)}</TableCell>
            <TableCell className={`text-right tabular-nums ${pctTone(stats.max)}`}>{fmtPct(stats.max)}</TableCell>
            <TableCell className={`text-right tabular-nums ${pctTone(stats.min)}`}>{fmtPct(stats.min)}</TableCell>
            <TableCell className="text-right tabular-nums">{stats.positiveCount}</TableCell>
            <TableCell className="text-right tabular-nums">{stats.negativeCount}</TableCell>
            <TableCell className="text-right tabular-nums text-emerald-600">{fmtPlainPct(stats.positiveAvg)}</TableCell>
            <TableCell className="text-right tabular-nums text-emerald-600">{fmtPlainPct(stats.positiveMax)}</TableCell>
            <TableCell className="text-right tabular-nums text-emerald-600">{fmtPlainPct(stats.positiveMin)}</TableCell>
            <TableCell className="text-right tabular-nums text-red-600">{fmtPlainPct(stats.negativeAvg)}</TableCell>
            <TableCell className="text-right tabular-nums text-red-600">{fmtPlainPct(stats.negativeMax)}</TableCell>
            <TableCell className="text-right tabular-nums text-red-600">{fmtPlainPct(stats.negativeMin)}</TableCell>
        </TableRow>
    );
}

const pctTone = (value: number) => (value >= 0 ? "text-emerald-600" : "text-red-600");

function MetricCard({
    label,
    value,
    hint,
    tone,
}: {
    label: string;
    value: string;
    hint?: string;
    tone?: "positive" | "negative";
}) {
    const toneClass = tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : "";
    return (
        <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</span>
            {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
    );
}
